import React, { useState } from "react";
import { Calendar, MapPin, Users, ChevronLeft, ChevronRight, Image as ImageIcon, Sparkles, Clock, Eye, X, ZoomIn } from "lucide-react";

// Vite glob imports — auto-pick every image from each event folder in src/assets/Images/<Folder Name>/
const pharmechnov2017 = import.meta.glob(
  "../../../assets/Images/exhibition/pharmech_nov_2017_ahmedabad/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const pharmalivefeb2019 = import.meta.glob(
  "../../../assets/Images/exhibition/pharmalive_feb_2019_kerala/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const iphexjune2019 = import.meta.glob(
  "../../../assets/Images/exhibition/iphex_june_2019_gandhinagar/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const ihffnov2019 = import.meta.glob(
  "../../../assets/Images/exhibition/ihff_nov_2019_mumbai/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const pharmatech2022 = import.meta.glob(
  "../../../assets/Images/exhibition/pharmatech_2022_gandhinagar/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const iphexsep2022 = import.meta.glob(
  "../../../assets/Images/exhibition/iphex_sep_2022_noida/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const pharmalivemarch2023 = import.meta.glob(
  "../../../assets/Images/exhibition/pharma_live_march_2023_mumbai/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const pharmatechaug2023 = import.meta.glob(
  "../../../assets/Images/exhibition/pharmatech_aug_2023_gandhinagar/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const vitafoodfeb2025 = import.meta.glob(
  "../../../assets/Images/exhibition/vitafood_feb_2025_mumbai/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const vitafoodfeb2026 = import.meta.glob(
  "../../../assets/Images/exhibition/vitafood_feb_2026_mumbai/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);
const iphexsep2026 = import.meta.glob(
  "../../../assets/Images/exhibition/iphex_sep_2026_delhi/*.{jpg,jpeg,png,JPG,JPEG,PNG}",
  { eager: true, query: "?url", import: "default" }
);


// Sort by numeric prefix (1,2,...,10,11) so gallery order is natural
const toSortedImages = (modules, label) =>
  Object.entries(modules)
    .sort(([a], [b]) => {
      const na = parseInt(a.match(/(\d+)\.[^./]+$/)?.[1] ?? "9999", 10);
      const nb = parseInt(b.match(/(\d+)\.[^./]+$/)?.[1] ?? "9999", 10);
      if (na !== nb) return na - nb;
      return a.localeCompare(b);
    })
    .map(([, url], i) => ({ url, name: `${label} — ${i + 1}` }));

const EventList = () => {
  // Event categories — Nutralike exhibition / expo history
  // Kept in chronological order here (oldest -> newest); display order is
  // reversed further below so the newest year shows first in the UI.
  const events = [
    {
      id: 1,
      name: "PHARMECH NOV 2017",
      date: "2017",
      time: "10:00 AM - 6:00 PM",
      location: "Ahmedabad",
      attendees: "500+",
      images: toSortedImages(pharmechnov2017, "Pharmech Nov 2017"),
    },
    {
      id: 2,
      name: "PHARMA-LIVE FEB 2019",
      date: "2019",
      time: "10:00 AM - 6:00 PM",
      location: "Kerala",
      attendees: "450+",
      images: toSortedImages(pharmalivefeb2019, "Pharma-Live Feb 2019"),
    },
    {
      id: 3,
      name: "IPHEX JUNE 2019",
      date: "2019",
      time: "10:00 AM - 6:00 PM",
      location: "Gandhinagar",
      attendees: "400+",
      images: toSortedImages(iphexjune2019, "Iphex June 2019"),
    },
    {
      id: 4,
      name: "IHFF NOV 2019",
      date: "2019",
      time: "10:00 AM - 6:00 PM",
      location: "Mumbai",
      attendees: "380+",
      images: toSortedImages(ihffnov2019, "Ihff Nov 2019"),
    },
    {
      id: 5,
      name: "PHARMATECH 2022",
      date: "2022",
      time: "10:00 AM - 6:00 PM",
      location: "Gandhinagar",
      attendees: "350+",
      images: toSortedImages(pharmatech2022, "Pharmatech 2022"),
    },
    {
      id: 6,
      name: "IPHEX SEP 2022",
      date: "2022",
      time: "10:00 AM - 6:00 PM",
      location: "Noida",
      attendees: "300+",
      images: toSortedImages(iphexsep2022, "Iphex Sep 2022"),
    },
    {
      id: 7,
      name: "PHARMA-LIVE MARCH 2023",
      date: "2023",
      time: "10:00 AM - 6:00 PM",
      location: "MUMBAI",
      attendees: "280+",
      images: toSortedImages(pharmalivemarch2023, "Pharma-Live March 2023"),
    },
    {
      id: 8,
      name: "PHARMATECH AUG 2023",
      date: "2023",
      time: "10:00 AM - 6:00 PM",
      location: "Gandhinagar",
      attendees: "280+",
      images: toSortedImages(pharmatechaug2023, "Pharmatech Aug 2023"),
    },
    {
      id: 9,
      name: "VITAFOOD FEB 2025",
      date: "2025",
      time: "10:00 AM - 6:00 PM",
      location: "MUMBAI",
      attendees: "280+",
      images: toSortedImages(vitafoodfeb2025, "Vitafood Feb 2025"),
    },
    {
      id: 10,
      name: "VITAFOOD FEB 2026",
      date: "2026",
      time: "10:00 AM - 6:00 PM",
      location: "MUMBAI",
      attendees: "280+",
      images: toSortedImages(vitafoodfeb2026, "Vitafood Feb 2026"),
    },
    {
      id: 11,
      name: "IPHEX SEP 2026",
      date: "2026",
      time: "10:00 AM - 6:00 PM",
      location: "Delhi",
      attendees: "300+",
      images: toSortedImages(iphexsep2026, "Iphex Sep 2026"),
    },

  ];

  // Newest-first order for display (sidebar list + default selection),
  // without touching the original chronological "events" array above.
  const displayEvents = [...events].reverse();

  const [selectedEvent, setSelectedEvent] = useState(displayEvents[0]);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewImage =
    modalOpen && selectedEvent.images[previewIndex]
      ? selectedEvent.images[previewIndex]
      : null;

  // Split images into rows of 3
  const renderImageGrid = () => {
    const rows = [];
    for (let i = 0; i < selectedEvent.images.length; i += 3) {
      rows.push(selectedEvent.images.slice(i, i + 3));
    }
    return rows;
  };

  // Open modal with selected image index
  const openModal = (index) => {
    setPreviewIndex(index);
    setModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  // Close modal
  const closeModal = () => {
    setModalOpen(false);
    document.body.style.overflow = "auto";
  };

  // Prev / Next (wrap around)
  const goToPrev = (e) => {
    if (e) e.stopPropagation();
    const total = selectedEvent.images.length;
    if (total === 0) return;
    setPreviewIndex((i) => (i - 1 + total) % total);
  };

  const goToNext = (e) => {
    if (e) e.stopPropagation();
    const total = selectedEvent.images.length;
    if (total === 0) return;
    setPreviewIndex((i) => (i + 1) % total);
  };

  // Keyboard: Escape to close, arrows to navigate
  React.useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e) => {
      if (e.key === "Escape") closeModal();
      else if (e.key === "ArrowLeft") goToPrev();
      else if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, selectedEvent.images.length]);

  return (
    <div className="w-full bg-white">
      {/* Green Header Section */}
      <section className="w-full py-12 px-4 flex flex-col items-center text-center bg-[#355931]">
        {/* Heading */}
        <h2 className="text-white text-3xl md:text-5xl font-bold tracking-tight">
          Our Events
        </h2>

        {/* Divider Line */}
        <div className="w-20 h-1 bg-white mt-4 mb-5 rounded-full"></div>

        {/* Description */}
        <p className="text-gray-100 text-sm md:text-base max-w-4xl mx-auto px-4 leading-relaxed">
          Join us for a series of exciting and engaging events designed to inspire, connect, and elevate your journey with us. 
          Our events bring together passionate individuals, industry experts, and like-minded communities to create meaningful 
          experiences that go beyond expectations. Whether you're looking to learn something new, expand your network, or simply 
          be part of an energetic environment, our events offer the perfect platform to do so.
        </p>
      </section>

      {/* White Content Section */}
      <section className="w-full bg-white py-12 px-3 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Main Content: Sidebar + Image Grid */}
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Left Sidebar - Event Names */}
            <div className="lg:w-80 w-full">
              <div className="bg-gray-50 rounded-md overflow-hidden border border-gray-200 sticky top-6">
                <div className="bg-white px-5 py-4 border-b border-gray-200">
                  <h3 className="text-[#1a2e17] font-bold text-base flex items-center gap-2">
                    <Calendar size={18} className="text-[#355931]" />
                    Event Categories
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {displayEvents.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`cursor-pointer w-full text-left px-5 py-4 transition-all duration-200 group ${
                        selectedEvent.id === event.id
                          ? "bg-[#355931] text-white shadow-inner"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-semibold text-sm ${
                            selectedEvent.id === event.id ? "text-white" : "text-gray-800"
                          }`}>
                            {event.name}
                          </p>
                          <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs ${
                            selectedEvent.id === event.id ? "text-white/80" : "text-gray-500"
                          }`}>
                            <span className="flex items-center gap-1">
                              <MapPin size={10} />
                              {event.location.split(',')[0]}
                            </span>
                           
                          </div>
                        </div>
                        <ChevronRight 
                          size={16} 
                          className={`transition-all duration-200 ${
                            selectedEvent.id === event.id 
                              ? "text-white translate-x-0.5" 
                              : "text-gray-400 group-hover:text-[#355931] group-hover:translate-x-0.5"
                          }`}
                        />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Image Grid (3 per row) with image name overlay at bottom */}
            <div className="flex-1">
              {/* Event Details Header */}
              <div className="mb-6 px-1 pb-3 border-b border-gray-100">
                <h3 className="text-[#1a2e17] text-xl sm:text-2xl font-bold">
                  {selectedEvent.name}
                </h3>
                <div className="flex flex-wrap gap-4 sm:gap-6 mt-2 text-gray-500 text-xs sm:text-sm">
                    <span className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#355931]" />
                    {selectedEvent.location}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Calendar size={14} className="text-[#355931]" />
                    {selectedEvent.date}
                  </span>
                
                
                 
                </div>
              </div>

              {/* Dynamic Image Grid - 3 columns with image name overlay at bottom */}
              <div className="space-y-5">
                {renderImageGrid().map((row, rowIndex) => (
                  <div 
                    key={rowIndex} 
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  >
                    {row.map((image, imgIndex) => (
                      <div 
                        key={imgIndex}
                        className="group relative overflow-hidden rounded-md bg-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        {/* Image Container */}
                        <div className="w-full overflow-hidden">
                         <div className="shine">

                           <img
                            src={image.url}
                            alt={image.name}
                            className="w-full h-[300px] object-cover p-3 transition-transform duration-500 "
                            loading="lazy"
                          />
                         </div>
                        </div>
                        
                       
                        
                        {/* Hover overlay with eye icon - Click to open modal */}
                        <div
                          className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center"
                          onClick={() => openModal(rowIndex * 3 + imgIndex)}
                        >
                          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 hover:bg-white/30">
                            <Eye size={24} className="text-white" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Empty State / No Images Message */}
              {selectedEvent.images.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 bg-gray-50 rounded-xl">
                  <ImageIcon size={48} className="text-gray-300 mb-3" />
                  <p className="text-gray-400 text-sm">No images available for this event</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Image Preview Modal - Popup on Eye Click */}
      {modalOpen && previewImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md transition-all duration-300"
          onClick={closeModal}
        >
          <div
            className="relative w-[60vw] h-[80vh] bg-white rounded-xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 rounded-full p-2 transition-all duration-200 "
            >
              <X size={24} className="text-white" />
            </button>

            {/* Zoom Icon Badge + counter */}
            <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
              <div className="bg-black/50 rounded-full p-2">
                <ZoomIn size={20} className="text-white" />
              </div>
              <span className="bg-black/50 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                {previewIndex + 1} / {selectedEvent.images.length}
              </span>
            </div>

            {/* Prev Arrow */}
            {selectedEvent.images.length > 1 && (
              <button
                onClick={goToPrev}
                aria-label="Previous image"
                className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-20
                  w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center
                  bg-black/50 hover:bg-black/75 text-white rounded-full
                  transition-all duration-200 shadow-lg active:scale-95"
              >
                <ChevronLeft size={26} />
              </button>
            )}

            {/* Next Arrow */}
            {selectedEvent.images.length > 1 && (
              <button
                onClick={goToNext}
                aria-label="Next image"
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 z-20
                  w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center
                  bg-black/50 hover:bg-black/75 text-white rounded-full
                  transition-all duration-200 shadow-lg active:scale-95"
              >
                <ChevronRight size={26} />
              </button>
            )}

            {/* Image Container */}
            <div className="w-full h-full flex items-center justify-center p-4 bg-gray-50">
             <div className="shine max-h-full flex items-center justify-center">
               <img
                key={previewImage.url}
                src={previewImage.url}
                alt={previewImage.name}
                className="max-w-full max-h-[72vh] object-contain rounded-lg"
              />
             </div>
            </div>

            {/* Image Info Footer */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-lg sm:text-xl">
                    {previewImage.name}
                  </h3>
  
                </div>
                <div className="text-white/50 text-xs">
                  Click outside to close
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventList;